import { useTinyHost } from "@tiny/host-contract";
import React from "react";
import { SegmentView } from "@tiny/api";
import {
  useDeleteSegmentBySegmentIdMutation,
  useDeleteSegmentMutation,
  usePutSegmentFindFilterQuery,
} from "@tiny/api";
import { useRefreshRegister } from "@tiny/components/src/RefreshProvider";
import useDebounce from "@tiny/components/src/hooks/useDebounce";
import { useRefCallBack } from "@tiny/components/src/hooks/useRefCallBack";
import useSelectable from "@tiny/components/src/hooks/useSelectable";
import { AudienceListProps } from "./AudienceList.types";
import { SortOption } from "./AudienceFilter/AudienceFilter.types";
import useLocalStorage from "@tiny/components/src/hooks/useLocalStorage";

export const sortOptions: SortOption[] = [
  { label: "Name", value: ["name,ASC"] },
  { label: "Number of Customers", value: ["numberOfCustomers,DESC"] },
  { label: "Modified Date", value: ["updatedAt,DESC"], action: "Modified" },
  { label: "Created Date", value: ["createdAt,DESC"], action: "Created" },
];

const itemsPerPageOptions = [5, 10, 15, 20];

export function useAudienceList(props: AudienceListProps) {
  const {
    enableBulkDelete = false,
    enableSelectionActions = true,
    segmentTypes = ["UPLOAD", "INTEGRATION"],
    onAfterDelete,
    selectionDisabled,
    selected: selectedProp,
    setSelected: setSelectedProp,
    visible = true,
    // consumed by rows (not a DOM attr) — keep it out of boxProps, which is
    // spread onto a <Box> and would otherwise leak to the DOM.
    seeAllSubcribersOnUpload,
    ...boxProps
  } = props;

  const { deselectAll, selected, toggleSelect, isSelected } =
    useSelectable<SegmentView>({
      idKey: "id",
      selected: selectedProp,
      setSelected: setSelectedProp,
    });

  const { capabilities } = useTinyHost();
  const isAiTrafficControl = capabilities.aiTrafficControlEnabled;

  const [storeAudiencePageSize, setStoreAudiencePageSize] = useLocalStorage(
    "audienceListPageSize",
    itemsPerPageOptions[0]
  );

  const [searchKeyword, setSearchKeyword] = React.useState("");
  const searchTextName = useDebounce(searchKeyword);
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(storeAudiencePageSize);
  const [sortOption, setSortOption] = React.useState(sortOptions[2]);
  const countAllCustomers = React.useMemo(() => false, []);
  const [showMergeDialog, setShowMergeDialog] = React.useState<boolean>(false);
  const [showTrafficControlDialog, setShowTrafficControlDialog] =
    React.useState<boolean>(false);

  const [isAudienceSummaryOpen, setIsAudienceSummaryOpen] =
    React.useState(false);

  const [isSegmentPerformanceOpen, setIsSegmentPerformanceOpen] =
    React.useState(false);

  const onPageChange = React.useCallback((page: number) => {
    setPage(page);
  }, []);

  const onItemsPerPageChange = React.useCallback(
    (size: number) => {
      setSize(size);
      setStoreAudiencePageSize(size);
    },
    [setStoreAudiencePageSize]
  );

  const isItemSelected = React.useCallback(
    (item: SegmentView) => {
      return isSelected(item);
    },
    [isSelected]
  );

  const selectItem = React.useCallback(
    (item: SegmentView) => {
      toggleSelect(item, true);
    },
    [toggleSelect]
  );

  const unselectItem = React.useCallback(
    (item: SegmentView) => {
      toggleSelect(item, false);
    },
    [toggleSelect]
  );

  const unselectAll = React.useCallback(() => {
    deselectAll();
  }, [deselectAll]);

  const nrOfSelectedAudiences = React.useMemo(
    () => selected.length,
    [selected]
  );

  const toggleSelectItem = React.useCallback(
    (item: SegmentView) => {
      isItemSelected(item) ? unselectItem(item) : selectItem(item);
    },
    [isItemSelected, selectItem, unselectItem]
  );

  React.useLayoutEffect(() => {
    setPage(0);
  }, [searchTextName, size, sortOption]);

  const {
    data: segmentData,
    refetch: refetchSegment,
    isFetching: isSegmentLoading,
  } = usePutSegmentFindFilterQuery(
    {
      searchTextName,
      segmentTypes,
      aiEnabled: isAiTrafficControl ? false : undefined,
    },
    { page, size, sort: sortOption.value, countAllCustomers },
    {
      keepPreviousData: true,
    }
  );

  useRefreshRegister(async () => {
    await refetchSegment();
  });

  const reloadAudiences = useRefCallBack(
    React.useCallback(() => {
      refetchSegment();
    }, [refetchSegment])
  );

  const deleteAudienceMutation = useDeleteSegmentBySegmentIdMutation();

  const deleteAudiencesMutation = useDeleteSegmentMutation();

  const onDeleteAudience = React.useCallback(
    async (audience: SegmentView) => {
      audience.id &&
        (await deleteAudienceMutation.mutateAsync(
          { segmentId: audience.id },
          {
            onSuccess: () => {
              unselectItem(audience);
              reloadAudiences();
              onAfterDelete?.();
            },
          }
        ));
    },
    [deleteAudienceMutation, onAfterDelete, reloadAudiences, unselectItem]
  );

  const onDeleteAudiences = React.useCallback(() => {
    const ids = selected.map((item) => item.id!);
    ids.length > 0 &&
      deleteAudiencesMutation.mutateAsync(
        { data: { ids } },
        {
          onSuccess: () => {
            unselectAll();
            reloadAudiences();
            onAfterDelete?.();
          },
        }
      );
  }, [
    deleteAudiencesMutation,
    onAfterDelete,
    reloadAudiences,
    selected,
    unselectAll,
  ]);

  const isAudienceDeletable = React.useCallback(
    (audience: SegmentView) =>
      audience.segmentType !== "INTEGRATION" &&
      !audience.assignedTo?.length &&
      (audience.status === "COMPLETED" || audience.status === "FAILED"),
    []
  );

  const audiences = segmentData;

  const deleteDisabled = React.useCallback(
    () =>
      selected?.reduce(
        (acc: boolean, curr: SegmentView) =>
          acc ||
          curr?.segmentType === "INTEGRATION" ||
          !!curr?.assignedTo?.length ||
          curr.status !== "COMPLETED",
        false
      ),
    [selected]
  );

  const mergeDisabled = React.useCallback(
    () => selected?.some((curr: SegmentView) => curr.status !== "COMPLETED"),
    [selected]
  );

  const onClickMergeAudiences = React.useCallback(() => {
    setShowMergeDialog(true);
  }, []);

  const closeMergeDialog = React.useCallback(() => {
    setShowMergeDialog(false);
  }, []);

  const onClickTrafficControl = React.useCallback(() => {
    setShowTrafficControlDialog(true);
  }, []);

  const closeTrafficControl = React.useCallback(() => {
    setShowTrafficControlDialog(false);
  }, []);

  const isEmpty = !isSegmentLoading && segmentData?.content?.length === 0;

  const handleOpenAudienceSummary = () => {
    setIsAudienceSummaryOpen(true);
  };

  const handleCloseAudienceSummary = () => {
    setIsAudienceSummaryOpen(false);
  };

  const handleOpenSegmentPerformance = () => {
    setIsSegmentPerformanceOpen(true);
  };

  const handleCloseSegmentPerformance = () => {
    setIsSegmentPerformanceOpen(false);
  };

  const selectedSegmentIds = React.useMemo(() => {
    return selected?.map((audience) => audience?.id || "") || [];
  }, [selected]);

  return {
    isSegmentLoading,
    audiences,
    boxProps,
    deleteDisabled,
    closeMergeDialog,
    enableBulkDelete,
    enableSelectionActions,
    isAudienceDeletable,
    isEmpty,
    isItemSelected,
    itemsPerPage: size,
    itemsPerPageOptions,
    nrOfSelectedAudiences,
    onClickMergeAudiences,
    onDeleteAudience,
    onDeleteAudiences,
    onItemsPerPageChange,
    onPageChange,
    page,
    reloadAudiences,
    searchKeyword,
    selected,
    selectItem,
    selectionDisabled,
    setSearchKeyword,
    setSortOption,
    showMergeDialog,
    sortOption,
    sortOptions,
    toggleSelectItem,
    unselectAll,
    unselectItem,
    visible,
    mergeDisabled,
    selectedSegmentIds,
    isAudienceSummaryOpen,
    handleOpenAudienceSummary,
    handleCloseAudienceSummary,
    onClickTrafficControl,
    closeTrafficControl,
    showTrafficControlDialog,
    deselectAll,
    isSegmentPerformanceOpen,
    handleOpenSegmentPerformance,
    handleCloseSegmentPerformance,
  };
}
